import React from 'react';

import { Helpers } from '../../core/src/helpers';
import Breadcrumb from '../../shared/components/breadcrumb';
import SearchProvider from '../../search-filter/components/search-container';
import DLPProductList from './dlp-product-list';
import DLPSubscribe from './dlp-subscribe';
import DLPHeader from './dlp-header';
import DLPIntelContainer from './dlp-intel-container';
import DLPFooter from './dlp-footer';
import DLPBanner from './dlp-banner';


import '../css/dlp.less';
import '../css/dlp-v2.less';

/**
 * On initial page load Swiftype results are fetched all at once during the SSR stage.
 * That's done to capture the top most relevant results for the keyword and to
 * be able to sort those results through JavaScript later without hitting the
 * Swiftype API (it mixes irrelevant results into the list when sorting).
 *
 * Due to this fact pagination on DLP is entirely controlled by this component's state.
 * Also keep in mind that pagination works with SSR enabled only.
 */
class DLP extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            pagesRevealed: 1,
        };
        this.calcTotalPages = Helpers.memoize(this.calcTotalPages, (pageSize, itemCount) => `${pageSize}-${itemCount}`);
    }

    /**
     * Will reveal the next page of results.
     */
    showNextPage = () => {
        this.setState(prevState => ({
            pagesRevealed: prevState.pagesRevealed + 1,
        }));
    };

    getBreadcrumbs = (url, breadcrumbs) => {
        const { slugInfo } = this.props;

        return slugInfo && slugInfo.title
            ? breadcrumbs.concat([{ href: `${process.env.BASENAME}${url}`, text: slugInfo.title }])
            : breadcrumbs;
    };

    renderCSS(cssArg) {
        const { css } = this.props && (this.props.slugInfo || {});
        return (cssArg || css) && <style dangerouslySetInnerHTML={Helpers.createMarkup(cssArg || css)}></style>;
    }

    calcTotalPages(pageSize, itemCount) {
        let totalPages = Math.floor(itemCount / pageSize);
        if (itemCount % pageSize > 0) totalPages += 1;
        return totalPages;
    }

    /**
     * Tells whether [ LOAD MORE ] button should be shown or hidden.
     * Calculates from resultsLimit and pageSize.
     */
    showLoadMore = () => {
        const { pageSize, resultsLimit } = DLP_DEFAULT_SEARCH_OPTIONS;
        return this.calcTotalPages(pageSize, resultsLimit) === this.state.pagesRevealed;
    };

    render() {
        const { enableAppPDP = true, slugInfo = {}, ...rest } = this.props;
        const { key, components = {}, seo, css, templateKey } = slugInfo;
        const {
            breadcrumbs = Breadcrumb.DEFAULT_BREADCRUMB,
            hideCopyBlock = false,
            copyBlock = null,
            title = '',
            categories,
            wordCloud
        } = seo;
        const { dlpBanner = {}, leftMenu = {}, relatedPages = {} } = components;
        const { bannerImage: { url: bannerImageUrl } = {} } = dlpBanner || {};
        const { hideRelatedPages } = relatedPages || {};
        const { intelBanner } = dlpBanner;
        const { disableCompare = true } = leftMenu;

        return (
            <div className="dlp-wrapper v2">
                {/* if there's a banner image, render the styles element having the default styles, or the custom styles, in DLPBanner.
                    if there isn't any image provided, render it here instead
                */}
                {typeof bannerImageUrl === 'string' && (
                    <DLPBanner dlpBanner={dlpBanner} css={css} renderCSS={cssString => this.renderCSS(cssString)} />
                )}
                {!bannerImageUrl && css && this.renderCSS()}
                <div className="dlp-body">
                    <div className="dlp-header-and-intel-banner-area">
                        {title && (
                            <DLPHeader intelBanner={intelBanner} keyword={title} hideSeoParagraph={hideCopyBlock} seoParagraph={copyBlock} />
                        )}
                        {intelBanner && (
                            <DLPIntelContainer type="desktop" />
                        )}
                    </div>
                    <div className="dlp-product-area">
                        <DLPProductList
                            {...rest}
                            slugKey={key}
                            disableCompare={disableCompare}
                            pagesRevealed={this.state.pagesRevealed}
                            keyword={title}
                            intelBanner={intelBanner}
                        />
                    </div>
                    <DLPSubscribe keyword={key} breadcrumbs={breadcrumbs} categories={categories} />
                    {this.props.match.params.slug === slugInfo.key && (
                        <DLPFooter intelBanner={intelBanner} pageInfo={{ categories, templateKey, key }} match={rest.match} wordCloud={wordCloud} hideRelatedPages={hideRelatedPages} />
                    )}
                </div>
            </div>
        );
    }
}

const DLPContainer = ({ searchKey, searchFunc, ...rest }) => {
    return (
        <SearchProvider searchKey={searchKey} searchFunc={searchFunc}>
            <DLP {...rest} />
        </SearchProvider>
    );
};

export default DLPContainer;
